'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import Image from 'next/image';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const STEP = 0.5;

export default function SiteMapViewer() {
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastDistRef = useRef<number | null>(null);

  const clamp = (v: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

  const zoomIn = () => setScale((s) => clamp(s + STEP));
  const zoomOut = () => setScale((s) => clamp(s - STEP));
  const reset = () => {
    setScale(1);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  // Pinch-to-zoom via touch events
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length !== 2) return;
    e.preventDefault();
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (lastDistRef.current !== null) {
      const delta = dist - lastDistRef.current;
      setScale((s) => clamp(s + delta * 0.01));
    }
    lastDistRef.current = dist;
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastDistRef.current = null;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  return (
    <div className="bg-card rounded-2xl overflow-hidden card-shadow-lg border border-border mx-4">
      {/* Controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <p className="text-sm font-semibold text-foreground">Interactive Site Map</p>
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            className="p-2 rounded-lg bg-card border border-border hover:bg-muted active:scale-95 transition-all duration-150 disabled:opacity-40"
            aria-label="Zoom out"
          >
            <MagnifyingGlassMinusIcon className="w-4 h-4 text-foreground" />
          </button>
          <span className="text-xs font-bold text-muted-foreground tabular-nums w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            className="p-2 rounded-lg bg-card border border-border hover:bg-muted active:scale-95 transition-all duration-150 disabled:opacity-40"
            aria-label="Zoom in"
          >
            <MagnifyingGlassPlusIcon className="w-4 h-4 text-foreground" />
          </button>
          <button
            onClick={reset}
            className="p-2 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 active:scale-95 transition-all duration-150"
            aria-label="Reset zoom"
          >
            <ArrowsPointingOutIcon className="w-4 h-4 text-primary" />
          </button>
        </div>
      </div>

      {/* Map area — scrollable container, inner div expands with scale */}
      <div
        ref={containerRef}
        className="overflow-auto"
        style={{ height: '480px', cursor: scale > 1 ? 'grab' : 'default' }}
      >
        <div
          style={{
            width: `${scale * 100}%`,
            transformOrigin: 'top left',
            transition: 'width 0.2s ease',
          }}
        >
          <Image
            src="/assets/images/Fun_Day_Map__2_-1780922601474.png"
            alt="New Forest Family Fun Day Site Map - Ashley Recreation Ground, New Milton RFC"
            width={800}
            height={600}
            className="w-full h-auto object-contain"
            priority
          />
        </div>
      </div>

      <div className="px-4 py-2 bg-muted/20 border-t border-border">
        <p className="text-[10px] text-muted-foreground text-center">
          Pinch to zoom • Drag to pan • Use buttons above to adjust zoom
        </p>
      </div>
    </div>
  );
}